import { useEffect, useMemo, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
} from '@material-ui/core';

const TeamAwareClusterPickerFieldSchema = makeFieldSchema({
  output: z => z.string(),
  uiOptions: z =>
    z.object({
      teamField: z
        .string()
        .optional()
        .describe(
          'Name of the form field that contains the team entity ref (e.g. "group:default/devops"). Defaults to "team".',
        ),
      serviceTypeField: z
        .string()
        .optional()
        .describe(
          'Name of the form field that contains the service type used for default cluster selection. Defaults to "service_type".',
        ),
      labelKey: z
        .string()
        .optional()
        .describe(
          'Catalog label key on the (parent) team group used to detect subsidiaries. Defaults to "cathaypacific.com/subs".',
        ),
      labelAllowedValues: z
        .array(z.string())
        .optional()
        .describe(
          'If the parent group label value for labelKey is included here, the team is treated as a subsidiary.',
        ),
      subsidiariesClusters: z
        .array(z.string())
        .optional()
        .describe('Cluster IDs allowed for subsidiary teams.'),
      defaultClustersByServiceType: z
        .record(z.array(z.string()))
        .optional()
        .describe(
          'Map of service type -> allowed cluster IDs when the team is not a subsidiary.',
        ),
      clusterDisplayNames: z
        .record(z.string())
        .optional()
        .describe(
          'Optional map of cluster ID -> display name shown in the dropdown.',
        ),
    }),
});

export const TeamAwareClusterPickerSchema = TeamAwareClusterPickerFieldSchema;

type TeamAwareClusterPickerProps =
  typeof TeamAwareClusterPickerFieldSchema.TProps;

export const TeamAwareClusterPicker = (props: TeamAwareClusterPickerProps) => {
  const { onChange, required, rawErrors, formData, formContext, uiSchema } =
    props;
  const options = uiSchema?.['ui:options'] ?? {};
  const catalogApi = useApi(catalogApiRef);

  const teamField = options.teamField ?? 'team';
  const serviceTypeField = options.serviceTypeField ?? 'service_type';
  const labelKey = options.labelKey ?? 'cathaypacific.com/subs';

  const teamRef = (formContext?.formData as any)?.[teamField] as
    string | undefined;
  const serviceType = (formContext?.formData as any)?.[serviceTypeField] as
    string | undefined;

  const [allowed, setAllowed] = useState<string[]>([]);

  const defaultsByType = useMemo(
    () => options.defaultClustersByServiceType ?? {},
    [options.defaultClustersByServiceType],
  );
  const restricted = useMemo(
    () => options.subsidiariesClusters ?? [],
    [options.subsidiariesClusters],
  );

  useEffect(() => {
    let active = true;

    const run = async () => {
      const defaultAllowed = serviceType
        ? (defaultsByType[serviceType] ?? [])
        : [];

      if (!teamRef) {
        if (!active) return;
        setAllowed(defaultAllowed);

        if (!formData || !defaultAllowed.includes(formData)) {
          onChange(defaultAllowed[0] ?? (undefined as unknown as string));
        }

        return;
      }

      try {
        const entity = await catalogApi.getEntityByRef(teamRef);
        const parentRef = entity?.relations?.find(
          r => r.type === 'childOf' && r.targetRef.startsWith('group:'),
        )?.targetRef;
        const parentEntity = parentRef
          ? await catalogApi.getEntityByRef(parentRef)
          : entity;

        const labels = parentEntity?.metadata?.labels ?? {};
        const isSubs = Boolean(
          labels[labelKey] &&
          options.labelAllowedValues?.includes(labels[labelKey]),
        );

        const next = isSubs ? restricted : defaultAllowed;
        if (!active) return;
        setAllowed(next);

        if (!formData || !next.includes(formData)) {
          onChange(next[0] ?? (undefined as unknown as string));
        }
      } catch {
        if (!active) return;
        setAllowed(defaultAllowed);

        if (!formData || !defaultAllowed.includes(formData)) {
          onChange(defaultAllowed[0] ?? (undefined as unknown as string));
        }
      }
    };

    run();
    return () => {
      active = false;
    };
  }, [
    catalogApi,
    teamRef,
    serviceType,
    labelKey,
    formData,
    onChange,
    defaultsByType,
    restricted,
    options.labelAllowedValues,
  ]);

  const names = useMemo(
    () => options.clusterDisplayNames ?? {},
    [options.clusterDisplayNames],
  );

  return (
    <FormControl
      fullWidth
      required={required}
      error={Boolean(rawErrors?.length)}
    >
      <InputLabel
        htmlFor={props.idSchema?.$id ?? 'team-aware-cluster-picker'}
        id={`${props.idSchema?.$id ?? 'team-aware-cluster-picker'}-label`}
      >
        Primary Cluster
      </InputLabel>
      <Select
        id={props.idSchema?.$id ?? 'team-aware-cluster-picker'}
        labelId={`${props.idSchema?.$id ?? 'team-aware-cluster-picker'}-label`}
        value={formData ?? ''}
        onChange={e => onChange(String(e.target.value))}
      >
        {allowed.length === 0 ? (
          <MenuItem value="" disabled>
            No clusters available
          </MenuItem>
        ) : null}
        {allowed.map(v => (
          <MenuItem key={v} value={v}>
            {names[v] ?? v}
          </MenuItem>
        ))}
      </Select>
      {rawErrors?.length ? (
        <FormHelperText>{rawErrors.join(', ')}</FormHelperText>
      ) : null}
    </FormControl>
  );
};
